<template>
	<OnboardingFormStep :commit="commit">

		<template #heading>
			<h1 class="onboarding-step-heading">Here's what shooters should know about me...</h1>
		</template>

		<fieldset class="form-section">
			<legend>Tell Them Your Studio Name</legend>
			<TextField v-model="profile.studioName" required class="studio-name-field">
				Studio Name
			</TextField>
			<WebsiteField v-model="profile.studioWebsite" placeholder="https://" :error.sync="studioWebsiteError" required>
				<template #label>Studio Website</template>
				<template #description>Cannot be a Facebook page or Instagram profile.</template>
			</WebsiteField>
		</fieldset>

		<fieldset class="form-section">
			<legend>Upload Your Profile Photo</legend>
			<AvatarField v-model="avatarImage" :profile="profile" :error.sync="avatarError" />
		</fieldset>
		
		<fieldset class="form-section">
			<legend>
				Upload Your Studio Logo
				<span class="optional">Optional</span>
			</legend>
			<StudioLogoField :profile="profile" :image.sync="studioLogoImage" :error.sync="studioLogoError" />
		</fieldset>
	</OnboardingFormStep>
</template>

<script>
import onboarding from '@/mixins/onboarding';

const facebookHostnamePattern = /\b(?:facebook\.com|fb\.me)$/i;
const instagramHostnamePattern = /\b(?:instagram\.com)$/i;

export default {
  name: 'OnboardingStudio',
  mixins: [onboarding],
  onboardingStep: {
    isCompleted: ({ profile }) => {
      if (!profile || !profile.avatar || !profile.studioName) {
        return false;
      }
      const { studioWebsite } = profile;
      if (!studioWebsite) {
        return false;
      }
      const { hostname } = studioWebsite;
      return !facebookHostnamePattern.test(hostname) && !instagramHostnamePattern.test(hostname);
    },
    requires: ({ profile }) => profile,
  },
  data() {
    return {
      /** @type {string | null} */
      avatarError: null,
      /** @type {import('@/helpers/data').ImageRecord | null} */
      avatarImage: null,
      /** @type {string | null} */
      studioLogoError: null,
      /** @type {import('@/helpers/data').ImageRecord | null} */
      studioLogoImage: null,
      /** @type {string | null} */
      studioWebsiteError: null,
    };
  },
  methods: {
    /**
     * @returns {Promise<boolean | void>}
     */
    async commit() {
      /** @type {(typeof this) & import('@/mixins/onboarding').OnboardingComponent} */
      const { avatarImage, profile, studioLogoImage } = this;
      let hasError = false;
      if (!profile) {
        return false;
      }

      if (!profile.avatar && !avatarImage) {
        this.avatarError = 'Please choose a profile photo.';
        hasError = true;
      }

      const { studioName, studioWebsite } = profile;
      if (!studioName) {
        this.studioNameError = 'Please enter your studio name.';
        hasError = true;
      }

      if (!studioWebsite) {
        this.studioWebsiteError = 'Please enter your studio website.';
        hasError = true;
      } else {
        const { hostname } = studioWebsite;
        if (facebookHostnamePattern.test(hostname)) {
          this.studioWebsiteError = 'Your studio website cannot be a Facebook page.';
          hasError = true;
        } else if (instagramHostnamePattern.test(hostname)) {
          this.studioWebsiteError = 'Your studio website cannot be an Instagram profile.';
          hasError = true;
        }
      }

      if (hasError) {
        return false;
      }

      const store = this.$store;

      /** @type {Promise<unknown>[]} */
      const uploads = [];
      if (avatarImage) {
        uploads.push(
          this.upload(avatarImage.file).then((avatar) => {
            URL.revokeObjectURL(avatarImage.url);
            profile.avatar = avatar;
            this.avatarImage = null;
          })
        );
      }
      if (studioLogoImage) {
        uploads.push(
          this.upload(studioLogoImage.file).then((logo) => {
            URL.revokeObjectURL(studioLogoImage.url);
            profile.studioLogo = logo;
            this.studioLogoImage = null;
          })
        );
      }
      await Promise.all(uploads);

      const { avatar, studioLogo } = profile;
      await store.dispatch('profiles/updateActiveProfile', {
        avatar,
        studioLogo,
        studioName,
        studioWebsite,
      });

      this.$gtmDispatchOnboardingStepEvent(5, 'Profile Photo & Bio Input');

      return true;
    },
    /**
     * @param {File} file
     * @returns {Promise<string>}
     */
    upload(file) {
      return this.$store.dispatch('files/upload', file);
    },
  },
};
</script>
